/**
 * The knock being written, in the console (owner, 2026-08-26).
 *
 * A SIGNED-IN PERSON NEVER SEES THE DOOR POSTER. The at-door flow — the printed
 * plate, the intake's five questions, the sign-in that sending costs — is an
 * ACQUISITION surface: it exists to explain a door to somebody who has never
 * met one, and every step of it is a step towards an account. Run at somebody
 * who already has an account it explains nothing and costs a navigation out of
 * the product they were already standing in. So `/@maya` and the `+` dialog
 * both land here instead, and knocking becomes what it looks like everywhere
 * else in this shell: a thread, a plate at the top of it, and a box to type in.
 *
 * WHAT THE PLATE CARRIES, AND WHAT IT CANNOT. The name, the picture, the owner's
 * own line and the difficulty SENTENCE — the same four the public page prints,
 * from the same payload (`PublicDoor`), through the same prose builders, so the
 * two cannot drift. NEVER A MONEY FIGURE, and there is none to leak: a door has
 * no price and the payload carries no band (ruling C3, ruling CR).
 *
 * THE DRAFT IS SHELL STATE, MIRRORED TO `lib/draft.ts`. It lives in the CONTEXT
 * rather than in this component because this component unmounts the moment
 * somebody opens another conversation — that much has not changed. What changed
 * is the claim that used to sit here: that a knock written inside the console
 * leaves nothing, so the round-trip machinery was not its business.
 *
 * It leaves the origin at exactly one moment, and it is the worst possible one.
 * A knocker with no plan writes the whole knock, presses send, is refused, and
 * is offered a plan — and buying one is a trip to Stripe that comes back to a
 * bare `/`. Every word was gone (owner item 28). So the words are mirrored to
 * `localStorage` as they are typed, restored when a compose thread opens at that
 * handle, and cleared by the send that consumes them: React state for the
 * thread, the round-trip machinery for the round trip.
 *
 * THE WORDS ARE SENT, BECAUSE SEND WAS PRESSED (owner item 7, 2026-08-27).
 * Pressing send opens the hearing, the doorman asks its first question, and the
 * words that were typed go up as the sender's first turn — the stream runs, the
 * answer arrives, and nobody presses send twice.
 *
 * This pane used to hand them over as a DRAFT instead, on the rule that nothing
 * here says a sentence on somebody's behalf. The rule stands and is not what
 * that was: these are the exact words whose Send was just pressed, at this door,
 * one moment ago — delivering them is obeying the press, and holding them back
 * was the product deciding it had not heard it. The doorman's opening question
 * asks what this person wants and why here, which is what they had already
 * written.
 *
 * THE PRE-ACCOUNT DRAFT IS THE OTHER CASE AND KEEPS THE OLD BEHAVIOUR
 * (`started.tsx`'s `prefill`, fed by `pre-auth.tsx`). Those words were typed at
 * a poster and then survived a trip to an identity provider: minutes and an
 * origin ago, with no send at this door behind them. A rescued draft goes back
 * in the box. The difference is TIME, and it is the whole of the difference.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { lookUpHandleOnX, startKnock, type KnockStarted } from "../../lib/api";
import {
  clearHeldKnock,
  clearKnockDraft,
  loadAnonKey,
  saveKnockBody,
  savePendingKnock,
} from "../../lib/draft";
import { ApiError, doorQuery, handleExistsQuery, planQuery, qk } from "../../lib/queries";
import { useRoomAdded } from "../../lib/room";
import { SpentRow } from "../plan/usage-notice";
import { useFlag } from "../../lib/flags";
import { useSessionExpired } from "../../lib/session";
import { useMetered } from "./use-metered";
import { track } from "../../lib/telemetry";
import { Button, LinkButton } from "../ui/button";
import {
  ChatShell,
  DoorFace,
  DoormanBubble,
  NobBubbleFace,
  SystemRow,
  ThreadCard,
  type ThreadLine,
} from "../knock/chat";
import {
  AWAITING_ALLOWANCE_LINE,
  AWAITING_PLAN_LINE,
  CHECKING_SENDER_LINE,
  HEARING_OPENED_LINE,
  bioLine,
  difficultyLine,
  getsThroughLine,
  hearingIntro,
  openingLine,
} from "../knock/intake";
import {
  OWN_DOOR_ACTION,
  OWN_DOOR_ASIDE,
  OWN_DOOR_NOTE,
  doorMessageFor,
  messageFor,
} from "../knock/copy";
import { KnockStartedScreen } from "../knock/started";
import { TurnInput } from "../knock/turn-input";
import { useLandInSpot } from "../waiting/land";
import {
  LOOKUP_LIMIT_FREE_COPY,
  LOOKUP_LIMIT_PAID_COPY,
  NO_SUCH_HANDLE_COPY,
  REGISTER_FAILED_COPY,
  WaitingArea,
} from "../waiting/waiting-area";
import { useShellUi, type Compose } from "./state";
import { OWN_ID } from "../../lib/conversations";
import type { ShellWorld } from "./use-conversations";

export const FINDING_DOOR = "Finding the door.";
/** What the box says before anything is in it. The knock IS the message. */
export const COMPOSE_PLACEHOLDER = "Say what you want, in your own words.";
/**
 * Nob has nowhere to send a reply.
 *
 * A NEVER-FOR-NEW-ACCOUNTS BRANCH (ruling R-0826-3). Every account now proves an
 * address inside the onboarding run, before it can reach this pane at all, so
 * `me.contact_email` is present on the primary path always. The branch is kept
 * because "always" is a claim about the run and not about the row — a session
 * whose `/me` has not landed, or a row from before the step existed, would
 * otherwise get a composer whose send is silently refused. A knock whose reply
 * cannot land is a knock that wasted both sides' time, so the question is asked
 * rather than the box being offered.
 */
export const NEED_EMAIL_LINE = "Where should a reply reach you, if one comes?";
export const ADD_REPLY_ADDRESS = "Add your email";
/**
 * NOB HAS NEVER MET THIS PERSON (ruling R-0910-2 #4).
 *
 * A handle Nob knows nothing about is not a dead end and not a waiting room
 * either: it is an address that costs a cent to resolve, and the ruling put that
 * cent behind an explicit press. So the pane says the true thing — it has not
 * met them — and offers the one act that would change it. The lookup happens on
 * THAT TAP and on nothing else: not on the typeahead click that opened this
 * thread, not on the render, not on a retry of the door read.
 */
export function notMetLine(handle: string): string {
  return `Nob hasn't met @${handle} yet.`;
}
export const LOOK_THEM_UP = "Look them up on X";
export const BACK_TO_CONVERSATIONS = "Back to your conversations";

/** The four faces a compose thread can wear, in the order they are decided. */
export function ComposePane({ compose, world }: { compose: Compose; world: ShellWorld }) {
  const ui = useShellUi();
  const queryClient = useQueryClient();
  const sessionExpired = useSessionExpired();
  const land = useLandInSpot();
  const door = useQuery({ ...doorQuery(compose.handle), retry: false });
  /**
   * Whether this tier buys AI work at all (ruling C10) — the one fact that
   * decides what a refusal was about. `null` while nobody has answered. It is
   * read at the moment of the refusal and latched there (`awaitingPlan`), not
   * read again while the row is up.
   */
  const metered = useMetered();

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [awaiting, setAwaiting] = useState(false);
  /**
   * WHAT THE REFUSAL WAS ABOUT — DECIDED ONCE, WHEN IT ARRIVES (gauntlet walk
   * 2026-09-13, G-2b).
   *
   * The row used to read `metered` live, which made the sentence describing a
   * refusal that already happened change under the reader: a Free knocker was
   * refused for want of a PLAN, bought one, and the row they were still
   * looking at swapped to "your allowance has room" — the plan they had just
   * bought, reported as the thing they were missing. So the kind is latched
   * against this refusal and only a NEW refusal decides again (a subscriber
   * whose plan is spent really is waiting on an allowance, and `send` clears
   * it).
   *
   * `null` is nobody has answered yet, and it stays null until somebody does:
   * the allowance sentence covers that beat, exactly as it did before, because
   * telling a paying knocker they need a plan is the worse of the two
   * mistakes.
   */
  const [awaitingPlan, setAwaitingPlan] = useState<boolean | null>(null);
  /** The plan, for the one control's label on the refusal row — the server's word. */
  const plan = useQuery(planQuery());
  /**
   * The started knock, as the server answered it — the opening question and the
   * turn counter, so the handover into the hearing costs no second read. Local
   * on purpose: a pane that has been unmounted and come back has no business
   * replaying a moment, and `opening: null` is exactly how every other re-entry
   * reads the conversation back.
   */
  const [started, setStarted] = useState<KnockStarted | null>(null);
  /** The words that were in the box at the send, handed over once — as a turn. */
  const [handedOver, setHandedOver] = useState("");

  /**
   * Where a reply lands.
   *
   * `contact_email`, and there is nothing else to fall back to (ruling
   * R-0828-3). It is the address this person proved with a code; the auth
   * column that used to sit behind it held a Better Auth routing key at
   * `@x.placeholder.nob.me` and not a mailbox at all, and it is deleted rather
   * than left where a later edit could reach for it again.
   */
  const replyTo = world.me?.contact_email ?? "";

  /**
   * The same call for the first send and for the one the product makes on
   * somebody's behalf once room has arrived. A refusal of either opens
   * nothing: the row under the thread carries the one control (wave 61).
   */
  const send = useCallback(async () => {
    if (busy || !replyTo) return;
    const words = compose.draft;
    setBusy(true);
    setError(null);
    setAwaiting(false);
    setAwaitingPlan(null);

    // The anonymous key, when this browser has one. PROVENANCE, and the
    // spending of the server's held draft in the same breath: the endpoint
    // names the knock's origin from it and deletes the row (`knocks/start.ts`),
    // which is what stops the copy this pane just read back from reloading
    // itself over a conversation already under way (ruling R-0828-3).
    const anon = loadAnonKey();
    const r = await startKnock({
      door_handle: compose.handle,
      sender_profile: { reply_to_email: replyTo },
      ...(anon ? { anon_key: anon } : {}),
      // The words go up as the first turn the moment the knock opens, so the
      // doorman asks no opening question above them (wave 82): the knocker
      // would have read their own words as the answer to a question they
      // never saw.
      knocker_speaks_first: words.trim() !== "",
    });
    setBusy(false);

    if (!r.ok) {
      const { failure } = r;
      if (failure.code === "UNAUTHENTICATED") return sessionExpired();
      // THE CONVERSION GATE, AND IT IS AT THE SEND (§5.4, ruling R-0825-4).
      // Not before it: a Free knocker writes the whole knock and meets the
      // offer at the moment the money would actually move, which is the one
      // moment the offer is about something.
      //
      // AND IT IS THE ROW, NOT A SHEET THAT OPENS BY ITSELF (owner,
      // 2026-09-23; wave 61): "Button is better, keep it uniform." This
      // refusal used to throw the paywall up on the first `PLAN_EXHAUSTED`,
      // and below the top rung that sheet offered the upgrade and hid the
      // reset. Now it shows what every other conversation at the wall shows
      // — the sentence and one control, opening the plan view at its
      // options — and never what this knock would have cost.
      if (failure.code === "PLAN_EXHAUSTED") {
        setAwaiting(true);
        return;
      }
      setError(messageFor(failure));
      return;
    }

    // The knock exists. The id is what reopens the hearing on a reload, the
    // plan's period moved because the work is metered against it, and the
    // list gains a real outgoing row (`use-conversations.ts`).
    savePendingKnock({ id: r.started.knock.id, handle: compose.handle });
    // The pre-account stash has been handed back and sent on: a copy that
    // outlived this would put the same words into the next knock at this door.
    clearHeldKnock();
    // …and so has the round-trip copy. Words that have become a knock are not
    // a draft any more, and one left behind would be typed into the next one.
    clearKnockDraft();
    track({ name: "knock_started" });
    setHandedOver(words);
    setStarted(r.started);
    ui.setComposeDraft("");
    ui.composeStarted(r.started.knock.id);
    // The plan, and not a wallet: the knock's work is metered against this
    // period, so the ring in the header is stale the moment the knock opens.
    void queryClient.invalidateQueries({ queryKey: qk.plan() });
    void queryClient.invalidateQueries({ queryKey: qk.myKnocks() });
    // The check just ran, so the next knock in this window will not wait for
    // one (R-0906-3): re-read the viewer, or the waiting line would appear
    // again on a knock that opens straight away.
    void queryClient.invalidateQueries({ queryKey: qk.me() });
    // The list the sidebar draws: the knock is the outgoing side of a
    // thread now, and the row it joins — or opens — has to hear about it.
    void queryClient.invalidateQueries({ queryKey: qk.threads() });
  }, [busy, compose.draft, compose.handle, queryClient, replyTo, sessionExpired, ui]);

  /**
   * The latch closes on the first answer there is. A refusal can arrive before
   * the plan read does, and a refusal that never learns what it was about is
   * how the wrong sentence got printed in the first place.
   */
  useEffect(() => {
    if (!awaiting || awaitingPlan !== null || metered === null) return;
    setAwaitingPlan(metered === false);
  }, [awaiting, awaitingPlan, metered]);

  /**
   * EVERY KEYSTROKE, MIRRORED — see the note at the top of this file. Debounced
   * inside `saveKnockBody`, and skipped once the thread is a knock: from that
   * point the composer belongs to the hearing and the words are the server's.
   */
  useEffect(() => {
    if (compose.knockId) return;
    saveKnockBody(compose.handle, compose.draft);
  }, [compose.handle, compose.draft, compose.knockId]);

  /**
   * THE KNOCK OPENS WITHOUT A SECOND SEND (owner item 28).
   *
   * The old shape ended here: the 402 set `awaiting`, the plans were opened, the
   * upgrade landed — and `awaiting` was local state that nothing ever cleared,
   * so the thread sat under a sentence about waiting for money that had already
   * arrived. The person had to guess that pressing send again would work.
   *
   * What is watched is the BILLING PANEL, not the plan query, and that is a
   * deliberate choice between two honest signals. A plan read can change for
   * reasons that are not this person funding this knock — a background refetch,
   * a webhook landing late — and retrying a send off a cache invalidation means
   * retrying it at a moment nobody chose. The panel closing after having been
   * opened is an act: they went to sort the money out, and they are back. It is
   * also the signal that survives the case the query cannot see, which is
   * somebody buying nothing and simply closing the panel — the retry is refused
   * again and the row below offers the control.
   *
   * ONCE. The flag is spent before the send, so a panel opened and closed twice
   * is two deliberate acts rather than a loop.
   *
   * AND THE PURCHASE ITSELF IS THE OTHER ACT — the one the panel could not see
   * (gauntlet walk 2026-09-13 round two, G-2b). Buying a plan does NOT close
   * the billing panel: the card form closes and the panel stays up on its
   * celebration, which is right — somebody who has just paid should be told
   * so where they paid. But it means the signal above never arrives while the
   * buyer sits reading "Lite is on.", and the thread behind it goes on saying
   * the hearing opens once they are on a plan, at somebody who is now on one.
   * Two browser walks in two rounds found exactly that, and the interaction
   * test did not, because it pressed Escape on the panel — a step the flow
   * does not contain.
   *
   * So a checkout ending `paid` fires the retry on its own, wherever it was
   * started from and whatever stays on screen afterwards — and since wave 57
   * so does an upgrade bought on the plan view, which does not close the
   * panel. Both arrive as room added (`lib/room.ts`). It is the strongest version of the same signal the
   * panel was standing in for: not a cache that moved, but this person
   * completing a payment. The panel arm stays for the case that is not a
   * purchase at all — going to look at the money and coming back having bought
   * nothing, which is answered by the refusal and the control beneath it.
   */
  const billingOpen = ui.overlay.kind === "billing";
  const wentToFund = useRef(false);
  useEffect(() => {
    if (!awaiting) {
      wentToFund.current = false;
      return;
    }
    if (billingOpen) {
      wentToFund.current = true;
      return;
    }
    if (!wentToFund.current) return;
    wentToFund.current = false;
    void send();
  }, [awaiting, billingOpen, send]);
  useRoomAdded(() => {
    if (!awaiting) return;
    // Spent, so the panel closing behind the celebration is not a second
    // retry of a send that has already gone.
    wentToFund.current = false;
    void send();
  });

  /**
   * The hearing outranks the door it happens at: once a knock exists, this
   * thread IS that knock, and the plate above it has done its job.
   *
   * The PLATE ITSELF DOES NOT COME ALONG. It is a card with a picture on it,
   * and the only way to carry it into the transcript would be to render the
   * same four facts a second way, as bubbles — which is precisely the drift
   * `door-card.tsx` was written to end. What comes along is one line naming the
   * door, so the hearing does not begin out of nowhere; the door's name and
   * handle are in the pane's own header the whole time either way.
   *
   * Nothing at all on a re-entry: `started` is gone with the unmount, the
   * transcript is read back from the server, and a prelude invented over the
   * top of it would be this client narrating a conversation it did not witness.
   */
  if (compose.knockId) {
    const prelude: ThreadLine[] =
      started && door.data
        ? [
            { role: "doorman", content: openingLine(door.data) },
            { role: "notice", content: HEARING_OPENED_LINE },
          ]
        : [];
    return (
      <div className="h-full min-h-0 px-2 sm:px-4">
        <KnockStartedScreen
          key={compose.knockId}
          embedded
          knockId={compose.knockId}
          handle={compose.handle}
          doorName={door.data?.display_name ?? null}
          doorAvatar={door.data?.avatar ?? null}
          prelude={prelude}
          firstTurn={handedOver}
          // THE LINE MUST SURVIVE THE SEND (ruling R-0914-1 (11)). A knock that
          // has just started renders from `started` and never reads the
          // transcript, so the disclosure has to be handed over here or it
          // vanishes at exactly the moment the hearing begins. The door's copy
          // is the fallback for a re-entry, where `started` is gone with the
          // unmount and the transcript read has not answered yet.
          disclosure={started?.disclosure || door.data?.disclosure || ""}
          // `""` when the knocker spoke first, and not `null`: this knock has
          // just opened and there is nothing to read back — its first line is
          // the knocker's own, sent below as `firstTurn`.
          opening={started ? started.opening_message : null}
          turnsUsed={started ? started.knock.turns_used : null}
          maxTurns={started ? started.knock.max_turns : null}
          onSessionExpired={sessionExpired}
          onGone={() => ui.clearCompose()}
          onOpenPlans={() => ui.openBilling("plans", "options")}
          onStartTrial={() => ui.openBilling("plans", "trial")}
          onLeave={() => ui.setMobilePane("list")}
        />
      </div>
    );
  }

  if (door.isPending) {
    return <p className="nob-breathe px-6 py-10 text-sm text-faint">{FINDING_DOOR}</p>;
  }

  if (!door.data) {
    // Paused, gone, or never there — three different things, said as three
    // (FR-3.1), and "never there" is a waiting room rather than a dead end.
    const code = door.error instanceof ApiError ? door.error.code : "UNKNOWN";
    if (code === "DOOR_NOT_FOUND") {
      return (
        <div className="h-full min-h-0 overflow-y-auto">
          <NoDoorHere
            handle={compose.handle}
            onSessionExpired={sessionExpired}
            onClaim={() => ui.clearCompose()}
            // The spot exists, so this thread IS that spot: the list redraws
            // this row as a pre-knock under the same id and the pane follows
            // (`use-conversations.ts`, `waiting/land.ts`). Nothing navigates.
            onRegistered={land}
            onLeave={() => ui.clearCompose()}
          />
        </div>
      );
    }
    const known =
      code === "DOOR_CLOSED" || code === "DOOR_SUPPRESSED" || code === "DOOR_NOT_FOUND"
        ? code
        : "UNKNOWN";
    return (
      <div className="px-6 py-10">
        <p className="text-[15px] text-text-soft" role="alert">
          {doorMessageFor(known)}
        </p>
        <p className="mt-6">
          <LinkButton type="button" onClick={() => ui.clearCompose()}>
            {BACK_TO_CONVERSATIONS}
          </LinkButton>
        </p>
      </div>
    );
  }

  // The owner, at their own door. The server refuses a self-knock outright
  // (403 OWN_DOOR), so a composer here would be a box whose send is a
  // guaranteed refusal.
  if (world.handle && world.handle.toLowerCase() === compose.handle.toLowerCase()) {
    return (
      <div className="grid h-full min-h-0 place-items-center px-6">
        <div className="flex max-w-sm flex-col items-center gap-3 text-center">
          <DoorFace name={door.data.display_name} avatar={door.data.avatar} />
          <p className="nob-empty-title mt-1">{OWN_DOOR_ASIDE}</p>
          <p className="text-sm leading-snug text-muted">{OWN_DOOR_NOTE}</p>
          <div className="mt-2">
            <Button
              type="button"
              onClick={() => {
                ui.clearCompose();
                ui.setActive(OWN_ID);
              }}
            >
              {OWN_DOOR_ACTION}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const bio = bioLine(door.data);
  const difficulty = difficultyLine(door.data);

  return (
    <ChatShell
      fill
      header={null}
      composer={
        replyTo ? (
          <TurnInput
            value={compose.draft}
            onChange={ui.setComposeDraft}
            disabled={busy}
            turn={0}
            placeholder={COMPOSE_PLACEHOLDER}
            onSend={() => void send()}
          />
        ) : (
          // The address lives on the ACCOUNT page — a knocker composing their
          // first knock has no door page at all.
          <ReplyAddress onOpen={() => ui.openSettings("account")} />
        )
      }
    >
      {/* WHAT THEY ARE ABOUT TO TALK TO, BEFORE THEY SAY ANYTHING (EU AI Act
          Art. 50; ruling R-0914-1 (11)).
          IT BELONGS HERE AND NOT ONLY AFTER SEND. This is the draft state: the
          plate, the doorman's opening bubble and the composer, all on screen
          while the knock is still a Draft — so a stranger has already read the
          doorman talking before any knock payload exists. A disclosure that
          first appears after Send has been read after the thing it was meant to
          frame. It is the same bytes the knock payloads carry, from the door
          read (`routes/public.ts`), and it sits above the plate for the same
          reason it sits above the first row of a started thread. */}
      {door.data.disclosure ? (
        <p
          data-testid="ai-disclosure"
          className="mb-3 text-center text-[11px] leading-relaxed text-fainter"
        >
          {door.data.disclosure}
        </p>
      ) : null}

      {/* THE PLATE. The same four facts the public page prints, and the same
          builders — a fifth copy of this prose is how the two come to differ. */}
      <ThreadCard label={door.data.display_name}>
        <div className="flex items-start gap-3">
          <DoorFace name={door.data.display_name} avatar={door.data.avatar} />
          <div className="min-w-0">
            <p className="truncate text-[15px] font-semibold tracking-tight">
              {door.data.display_name}
            </p>
            <p className="truncate font-mono text-xs text-faint">@{door.data.handle}</p>
          </div>
        </div>
        <p className="mt-3 text-sm leading-relaxed text-text-soft">{openingLine(door.data)}</p>
        {bio && <p className="mt-2 text-sm leading-relaxed text-muted">{bio}</p>}
        <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-muted">
          {getsThroughLine(door.data)}
        </p>
        {/* Difficulty is PROSE the server decided, and it is the only thing on
            this card that is about how hard the door is. There is no figure
            anywhere near it and none in the payload to put there. */}
        {difficulty && <p className="mt-2 text-sm leading-relaxed text-faint">{difficulty}</p>}
      </ThreadCard>

      {/* Nob's face beside its run (wave 90): the introduction and a refusal
          under it are one run unless the room's check stands between them. */}
      <DoormanBubble
        gutter={error && !(busy && world.me?.sender_check_due === true) ? null : <NobBubbleFace />}
      >
        {hearingIntro(door.data)}
      </DoormanBubble>

      {/* THE CHECK THE DOOR IS WAITING ON (ruling R-0906-3). The start request
          now waits for the sender check, which runs once per account
          (R-0911-1), so an account's first knock takes seconds no later one
          will. It is said as the ROOM — the same centred, breathing row a
          knock being found gets — because
          the doorman has not been asked anything yet and a bubble here would
          put words in its mouth. It stands exactly where its opening question
          is about to, and it goes when that arrives. */}
      {busy && world.me?.sender_check_due === true && (
        <SystemRow ring>{CHECKING_SENDER_LINE}</SystemRow>
      )}

      {error && (
        <DoormanBubble alert gutter={<NobBubbleFace />}>
          {error}
        </DoormanBubble>
      )}

      {/* The plan could not cover the hearing. No figure in the thread — the
          offer is the plan sheet's, and the sheet names a plan, not a knock.

          One line and one control (owner, 2026-09-23; wave 57): "Get more
          usage", or "Start a plan" on a trial or on Free, opening the plan
          sheet at its options. It used to be "Try again", offered only once
          the automatic retry had been refused; the retry still runs by itself
          when room arrives, and the composer's own send is still there. A
          sentence that describes a state nobody can leave is not a status, it
          is a dead end — and this row always says how to leave it. */}
      {awaiting && (
        <SpentRow
          line={awaitingPlan === true ? AWAITING_PLAN_LINE : AWAITING_ALLOWANCE_LINE}
          plan={plan.data?.plan}
          onAction={() => ui.openBilling("plans", "options")}
          trial={plan.data?.trial ?? null}
          onStartTrial={() => ui.openBilling("plans", "trial")}
        />
      )}
    </ChatShell>
  );
}

/**
 * THE TWO "THERE IS NO DOOR HERE" ROOMS, AND THE PRESS BETWEEN THEM (ruling
 * R-0910-2 #4).
 *
 * `DOOR_NOT_FOUND` covers two situations that used to be answered the same way,
 * by opening the waiting room — which quietly ran X's PAID user lookup for any
 * handle Nob had never seen, on every render, for anybody who clicked a
 * "Knock at @x" row or followed a link. About a cent each, and a mistyped handle
 * paid it again on every retry.
 *
 * So the FREE check runs first (`handleExistsQuery`: Nob's records, the miss
 * cache, X's keyless embed endpoint) and decides which room this is:
 *
 *   Nob knows them        → the waiting room, immediately and for nothing.
 *   Nobody holds it       → one sentence, and a way back. No page invented.
 *   Somebody does, or X
 *   would not say         → the OFFER: what it costs is a press, and the press
 *                           is the intent the paid call is allowed on.
 *
 * The press is the whole design. A lookup that happens on a click, a keystroke
 * or a mount is a lookup nobody asked for, and it is the one this ruling exists
 * to stop; a lookup that happens on a button reading "Look them up on X" is one
 * somebody chose. The refusals it can come back with are said as sentences with
 * a way back, never as "Nob is not answering" — Nob answered.
 */
function NoDoorHere({
  handle,
  onSessionExpired,
  onClaim,
  onRegistered,
  onLeave,
}: {
  handle: string;
  onSessionExpired: () => void;
  onClaim: () => void;
  onRegistered: ReturnType<typeof useLandInSpot>;
  onLeave: () => void;
}) {
  const queryClient = useQueryClient();
  const exists = useQuery({ ...handleExistsQuery(handle), retry: false });
  /** Ruling R-0912-1: a kill switch on the one paid press, defaulting ON. */
  const lookupEnabled = useFlag("x_lookup", true);
  const [busy, setBusy] = useState(false);
  /** The paid lookup landed and there is no door: this IS the waiting room now. */
  const [waiting, setWaiting] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const look = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setRefusal(null);
    const r = await lookUpHandleOnX(handle);
    setBusy(false);
    if (r.ok) {
      // A DOOR, after all — the commonest case being a handle that moved. The
      // door read is re-run rather than navigated to: this thread is already at
      // this address, and what changed is what is behind it.
      if (r.door) {
        void queryClient.invalidateQueries({ queryKey: qk.door(handle) });
        void queryClient.invalidateQueries({ queryKey: qk.handleExists(handle) });
        return;
      }
      setWaiting(true);
      return;
    }
    switch (r.code) {
      case "UNAUTHENTICATED":
        return onSessionExpired();
      case "HANDLE_UNRESOLVED":
        return setRefusal(NO_SUCH_HANDLE_COPY);
      case "LOOKUP_LIMIT_FREE":
        return setRefusal(LOOKUP_LIMIT_FREE_COPY);
      case "LOOKUP_LIMIT_PAID":
        return setRefusal(LOOKUP_LIMIT_PAID_COPY);
      default:
        return setRefusal(REGISTER_FAILED_COPY);
    }
  }, [busy, handle, onSessionExpired, queryClient]);

  if (exists.isPending) {
    return <p className="nob-breathe px-6 py-10 text-sm text-faint">{FINDING_DOOR}</p>;
  }

  if (waiting || exists.data?.known === true) {
    return (
      <WaitingArea
        handle={handle}
        onSessionExpired={onSessionExpired}
        onClaim={onClaim}
        onRegistered={onRegistered}
      />
    );
  }

  const line = refusal ?? (exists.data?.exists === false ? NO_SUCH_HANDLE_COPY : null);
  if (line) {
    return (
      <div className="px-6 py-10">
        <p className="text-[15px] text-text-soft" role="alert">
          {line}
        </p>
        <p className="mt-6">
          <LinkButton type="button" onClick={onLeave}>
            {BACK_TO_CONVERSATIONS}
          </LinkButton>
        </p>
      </div>
    );
  }

  return (
    <div className="px-6 py-10">
      <p className="text-[15px] text-text-soft">{notMetLine(handle)}</p>
      {/*
       * THE ONE FLAGGED SURFACE (ruling R-0912-1 rule 2), and it is a KILL
       * SWITCH, so it defaults ON: with `x_lookup` unset — which is every
       * session until somebody creates the flag — the tap is exactly where it
       * was. What it buys is the ability to stop paying X per resolution
       * without a deploy, because the tap is the one place in the product that
       * spends a cent on somebody else's API on a stranger's press.
       *
       * The sentence above stays either way. "Nob hasn't met them" is true
       * whether or not we are willing to go and look, and a screen with the
       * explanation removed alongside the button would leave a dead end.
       */}
      {lookupEnabled && (
        <p className="mt-5">
          <Button type="button" disabled={busy} onClick={() => void look()}>
            {LOOK_THEM_UP}
          </Button>
        </p>
      )}
      <p className="mt-6">
        <LinkButton type="button" onClick={onLeave}>
          {BACK_TO_CONVERSATIONS}
        </LinkButton>
      </p>
    </div>
  );
}

/**
 * No address on file, so there is nowhere for a reply to land and no composer.
 *
 * IT ASKS HERE AND IS ANSWERED WHERE IT LIVES — the same rule the doorman's
 * standing prompts keep since 2026-08-25. The address is a field on the door's
 * own settings page, and a second copy of it at the bottom of a chat is how the
 * two come to ask for different things.
 */
function ReplyAddress({ onOpen }: { onOpen: () => void }) {
  return (
    <div className="flex flex-wrap items-center gap-3 pb-1">
      <p className="min-w-0 flex-1 text-sm leading-snug text-muted">{NEED_EMAIL_LINE}</p>
      <Button type="button" className="h-9 shrink-0 px-4" onClick={onOpen}>
        {ADD_REPLY_ADDRESS}
      </Button>
    </div>
  );
}
